/**
 * ChainTargetingSystem — finds valid chain targets for abilities that
 * bounce between enemies.
 *
 * Used by Lightning's Arc/Chain Lightning (nearest-hop single mode)
 * and Poison's Contagion (broadcast-all mode). Supports damage
 * retention per hop, status-based range bonuses, relay conditions,
 * and target filtering by status.
 *
 * Pure targeting logic — no Phaser dependencies. Takes an iterable
 * of target candidates with position and status info.
 *
 * @see ADR-064 (attunement shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal target interface for chain targeting. */
export interface ChainTarget {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  /** Status effect IDs currently active on this target. */
  readonly activeStatuses: string[];
}

export interface ChainTargetResult {
  /** Targets in chain order, with their hop index and accumulated damage multiplier. */
  targets: Array<{ target: ChainTarget; order: number; damageMultiplier: number }>;
  /** Total accumulated damage multiplier after all hops. */
  totalDamageMultiplier: number;
}

export interface ChainConfig {
  readonly originX: number;
  readonly originY: number;
  /** Max chain hops (single mode) or max targets (all mode). */
  readonly maxTargets: number;
  /** Range per hop in tiles. */
  readonly maxRangeTiles: number;
  /** Damage multiplier per hop (e.g., 0.7 = 30% falloff). */
  readonly damageRetention: number;
  /** Entity to exclude from targeting (the caster or origin). */
  readonly sourceEntityId?: string;

  /** 'single' = nearest hop from previous target. 'all' = broadcast to all in range. */
  readonly mode: 'single' | 'all';

  /** Bonus range when chaining through targets with a specific status. */
  readonly bonusRangeForStatus?: {
    readonly statusId: string;
    readonly rangeMultiplier: number;
  };

  /** Only chain to targets that have this status. */
  readonly requireStatus?: string;
  /** Skip targets that have this status. */
  readonly excludeStatus?: string;

  /** Relay condition — targets with this status act as relays with bonus retention. */
  readonly relayCondition?: {
    readonly statusId: string;
    readonly bonusRetention?: number;
  };

  /**
   * Extra chain hops granted when the chain passes through a target with
   * `rodStatusId`. Each rod-marked target adds `rodBonusChain` hops to the
   * effective chain length. Used by Chain Lightning + Ionized interaction.
   */
  readonly rodBonusChain?: number;
  /** Status ID that marks a target as a "lightning rod" for bonus hops. */
  readonly rodStatusId?: string;

  /** Range multiplier applied only to the first hop (e.g., 2.0 = double range). */
  readonly firstHopRangeMultiplier?: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

import { TILE_SIZE } from '../utils/Constants.js';


// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class ChainTargetingSystem {
  /**
   * Find chain targets starting from the origin point.
   *
   * In 'single' mode: hops to nearest valid enemy, then nearest from there,
   * up to maxTargets hops. Each hop applies damageRetention.
   *
   * In 'all' mode: broadcasts to all valid enemies within range of the
   * origin simultaneously. Each target gets one hop of damageRetention.
   */
  findChainTargets(
    config: ChainConfig,
    candidates: Iterable<ChainTarget>,
  ): ChainTargetResult {
    const candidateArray = Array.from(candidates);
    const result: ChainTargetResult = {
      targets: [],
      totalDamageMultiplier: 1,
    };

    if (config.mode === 'all') {
      return this.findAllMode(config, candidateArray);
    }
    return this.findSingleMode(config, candidateArray);
  }

  // -----------------------------------------------------------------------
  // Single mode — nearest hop chain
  // -----------------------------------------------------------------------

  private findSingleMode(
    config: ChainConfig,
    candidates: ChainTarget[],
  ): ChainTargetResult {
    const result: ChainTargetResult = {
      targets: [],
      totalDamageMultiplier: 1,
    };

    const visited = new Set<string>();
    if (config.sourceEntityId) {
      visited.add(config.sourceEntityId);
    }

    let currentX = config.originX;
    let currentY = config.originY;
    let currentMultiplier = 1;
    let currentRangePx = config.maxRangeTiles * TILE_SIZE;
    let effectiveMaxTargets = config.maxTargets;

    for (let hop = 0; hop < effectiveMaxTargets; hop++) {
      let nearest: ChainTarget | null = null;
      let nearestDist = Infinity;

      for (const candidate of candidates) {
        if (visited.has(candidate.id)) continue;
        if (!this.isValidTarget(candidate, config)) continue;

        const dist = this.distance(currentX, currentY, candidate.x, candidate.y);

        // Apply first-hop range multiplier (e.g., Conducted conductive field bonus)
        let effectiveRange = currentRangePx;
        if (hop === 0 && config.firstHopRangeMultiplier) {
          effectiveRange *= config.firstHopRangeMultiplier;
        }

        // Apply bonus range if the current hop origin has the qualifying status
        if (config.bonusRangeForStatus && hop > 0) {
          // Check if the previous target (now current position) had the status
          const prevTarget = result.targets[hop - 1]?.target;
          if (prevTarget && prevTarget.activeStatuses.includes(config.bonusRangeForStatus.statusId)) {
            effectiveRange *= config.bonusRangeForStatus.rangeMultiplier;
          }
        }

        if (dist <= effectiveRange && dist < nearestDist) {
          nearest = candidate;
          nearestDist = dist;
        }
      }

      if (!nearest) break;

      visited.add(nearest.id);

      // Rod bonus: targets with rodStatusId grant extra chain hops
      if (config.rodBonusChain && config.rodStatusId &&
          nearest.activeStatuses.includes(config.rodStatusId)) {
        effectiveMaxTargets += config.rodBonusChain;
      }

      // Apply retention for this hop
      currentMultiplier *= config.damageRetention;

      // Check relay condition — relay targets give bonus retention
      if (config.relayCondition && nearest.activeStatuses.includes(config.relayCondition.statusId)) {
        const bonus = config.relayCondition.bonusRetention ?? 0;
        currentMultiplier *= (1 + bonus);
      }

      result.targets.push({
        target: nearest,
        order: hop,
        damageMultiplier: currentMultiplier,
      });

      currentX = nearest.x;
      currentY = nearest.y;
    }

    result.totalDamageMultiplier = currentMultiplier;
    return result;
  }

  // -----------------------------------------------------------------------
  // All mode — broadcast to all in range
  // -----------------------------------------------------------------------

  private findAllMode(
    config: ChainConfig,
    candidates: ChainTarget[],
  ): ChainTargetResult {
    const result: ChainTargetResult = {
      targets: [],
      totalDamageMultiplier: config.damageRetention,
    };

    const rangePx = config.maxRangeTiles * TILE_SIZE;
    let count = 0;

    // Sort by distance for deterministic ordering, then cap at maxTargets
    const withDist = candidates
      .filter(c => {
        if (config.sourceEntityId && c.id === config.sourceEntityId) return false;
        if (!this.isValidTarget(c, config)) return false;
        const dist = this.distance(config.originX, config.originY, c.x, c.y);
        return dist <= rangePx;
      })
      .map(c => ({
        candidate: c,
        dist: this.distance(config.originX, config.originY, c.x, c.y),
      }))
      .sort((a, b) => a.dist - b.dist);

    for (const { candidate } of withDist) {
      if (count >= config.maxTargets) break;

      result.targets.push({
        target: candidate,
        order: count,
        damageMultiplier: config.damageRetention,
      });
      count++;
    }

    return result;
  }

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  private isValidTarget(candidate: ChainTarget, config: ChainConfig): boolean {
    if (config.requireStatus && !candidate.activeStatuses.includes(config.requireStatus)) {
      return false;
    }
    if (config.excludeStatus && candidate.activeStatuses.includes(config.excludeStatus)) {
      return false;
    }
    return true;
  }

  private distance(x1: number, y1: number, x2: number, y2: number): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
